import Image from 'next/image';
import { render } from 'storyblok-rich-text-react-renderer';
import React from 'react';
import { resolveWebLink } from '@/storyblok/utils/link';
import Link from 'next/link';
import { DAVINCI_CDN } from '@/app/_constants/defaultValues';
import { makeRichTextRenderOptions } from '@/storyblok/utils/richText';
import { FooterLink } from '@/app/_components/layouts/footer/footerLink';
import { FooterCookieButton } from '@/app/_components/layouts/footer/footerCookieButton';
import { FooterWrapperComponentProps } from '@/app/_components/layouts/footer/types';
import { AccessiWayWidget } from '@/app/_components/widgets/accessWidget';
import clsx from 'clsx';

const FooterTop: React.FC<FooterWrapperComponentProps> = ({ component: footer, story, theme }) => (
  <footer className="flex flex-col">
    <div className="px-4 py-10 xl:px-20">
      <div className="flex flex-col gap-7 lg:grid lg:grid-cols-3 lg:items-center lg:justify-between xl:mx-auto xl:max-w-screen-xl">
        <div className="flex flex-col gap-5 lg:col-start-1">
          <Image src={`${DAVINCI_CDN}/elty/elty-logo.svg`} height={33} width={108} alt="Elty logo" />
          <div className="text-2xl font-medium text-zaffiro-900 lg:hidden">{render(footer.slogan, makeRichTextRenderOptions(story))}</div>
        </div>

        <span className="lg:hidden">
          <AccessiWayWidget size="small" />
        </span>

        <div className="flex flex-col gap-4 lg:col-span-2 lg:col-start-2 lg:flex-row lg:items-center lg:justify-between lg:gap-7">
          <div className="text-sm font-semibold text-zaffiro-900">{render(footer.appDownloadCta, makeRichTextRenderOptions(story))}</div>
          <div className="flex flex-row gap-2">
            <Link href={footer.appAndroidUrl?.url} target="_blank" rel="noopener noreferrer" className="h-10">
              <Image
                src={`${DAVINCI_CDN}/elty/download-on-google-play.png`}
                height={56}
                width={183}
                alt="Scarica l'applicazione dal Google Play Store"
                className="h-10 w-auto"
              />
            </Link>
            <Link href={footer.appIosUrl?.url} target="_blank" rel="noopener noreferrer" className="h-10">
              <Image
                src={`${DAVINCI_CDN}/elty/download-on-app-store.png`}
                height={56}
                width={184}
                alt="Scarica l'applicazione dall'App Store"
                className="h-10 w-auto"
              />
            </Link>
          </div>

          <ul className="mt-9 flex flex-row gap-3 lg:mt-0">
            {footer.socials?.map(social => (
              <li key={social._uid} title={social.Name}>
                {social.Link && (
                  <Link
                    href={resolveWebLink(social.Link, { additional: { name: 'footer social', social }, story, component: footer })}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {social.Icon && social.Name && <Image src={social.Icon?.filename} alt={social.Name ?? ''} width={32} height={32} />}
                  </Link>
                )}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>

    <hr
      className={clsx(
        'w-full',
        theme === 'dark' && 'border-b-[3px] border-brandGreen-400',
        theme === 'light' && 'border-b-1 border-[#627282]',
      )}
    />

    <div
      className={clsx(
        'flex w-full flex-col gap-9 px-4 pt-12 xl:px-20',
        theme === 'dark' && 'bg-primary text-white',
        theme === 'light' && 'bg-white text-primary',
      )}
    >
      <div className="w-full justify-between lg:grid lg:grid-cols-4 lg:gap-9 xl:mx-auto xl:max-w-screen-xl">
        <div className="flex flex-col justify-between gap-4 text-2xl lg:col-span-2 lg:col-start-1 lg:row-start-1">
          <span className="hidden font-bold lg:block">{render(footer.slogan, makeRichTextRenderOptions(story))}</span>

          <span className="hidden lg:block">
            <AccessiWayWidget size="small" />
          </span>

          <div className="mb-8 hidden flex-col gap-4 text-xs lg:mb-0 lg:flex [&>p]:leading-[1.125rem]">
            {render(footer.legalText, makeRichTextRenderOptions(story))}
          </div>
        </div>

        <nav className="lg:col-span-2 lg:col-start-3 lg:row-start-1">
          <ul className="flex flex-col gap-8 lg:flex-row lg:justify-between">
            {footer.sections?.map(section => (
              <li key={section._uid} className="flex flex-col gap-5">
                <h2 className="text-sm font-extrabold uppercase text-elty-green">{section.title}</h2>
                <nav>
                  <ul role="menu" className="flex flex-col gap-2">
                    {section.links?.map(link => (
                      <li key={link._uid} role="presentation">
                        {link.component === 'Link' && <FooterLink link={link} story={story} />}
                        {link.component === 'footerButton' && <FooterCookieButton component={link} story={story} />}
                      </li>
                    ))}
                  </ul>
                </nav>
              </li>
            ))}
          </ul>
        </nav>
      </div>
    </div>
  </footer>
);

export default FooterTop;
